<script>
export default {
  title: 'Project 2: Debugging',
  starterId: 'proj2.zip'
}
</script>

<template>
  <section>
    <p>No doubt the program for the previous programming assignment gave you some
      practice in debugging. This assignment will give you more. You are to find
      some bugs we've installed in a program. <code>gdb</code> will again be
      helpful.</p>

    <article>
      <h2>Readings</h2>
      <ul>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/idot-basics/pcp3-chp-5"><cite>Practical C Programming (3rd Edition)</cite> Sections 5.1 - 5.5</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/idot-basics/pcp3-chp-7"><cite>Practical C Programming (3rd Edition)</cite> Chapter 7</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/iidot-simple-programming/pcp3-chp-13"><cite>Practical C Programming (3rd Edition)</cite> Sections 13.0 - 13.5</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/iidot-simple-programming/pcp3-chp-15"><cite>Practical C Programming (3rd Edition)</cite> Chapter 15</a></li>
      </ul>
    </article>

    <article>
      <h2>Starter Code</h2>

      <p>
        Please download the starter code from above. It should contains a single file
        called <code>generate.c</code>.
      </p>
    </article>

    <article>
      <h2>Background</h2>

      <p>This assignment provides an introduction to some of the major features
        of <code>gdb</code> (the GNU debugger) by leading you through the debugging
        process of a slightly buggy program. A debugger allows the programmer to
        stop a program in the middle of its execution so that he or she may observe
        how the program branches and executes. Debuggers can help speed the process
        of isolating and correcting bugs.</p>

      <p>Follow the instructions outlined in the assignment in order to debug the
        provided program, and answer the questions throughout the assignment in a
        separate document. You will be required to present the corrected code (make
        sure to keep track of the corrections you make!) and answers to the
        questions in this assignment. The questions in this assignment are meant to
        be thought-provoking. They illustrate important aspects of the C language.
        If you experience problems, do not hesitate to ask a tutor for help!</p>

      <p>Students who do not wish to use <code>gdb</code> or students who use
        their own development package will need to follow the steps dictated in the
        instructions and demonstrate identical functionality in the debugger of
        their choice (e.g. via screenshots). If you can't provide such
        documentation, you will need to use <code>gdb</code>.</p>

      <p>The code associated with this assignment is a short program whose
        purpose is to turn regular phone numbers into "words." For example, the
        Federal Express customer service number is 1-800-GO-FED-EX, translated from
        1-800-46-333-39. The provided program, when working properly, will provide
        all possible "words" that have no more than a user-specified number of
        consonants. (Too many consonants may make the "word" unpronounceable.)
        Below is a transcript of how the program should work, with user input
        appearing in boldface:</p>

      <pre v-highlightjs><code class="plain">This program finds words that can be associated with phone numbers.
What is the maximum number of consonants you would like
the generated word to possess?
(note: 0's and 1's are considered consonants)
<span style="font-weight: bold">4</span>
Please enter a 7 digit phone number with dashes removed
<span style="font-weight: bold">4460175</span>
iio01pj  iio01pk  iio01pl  iio01rj  iio01rk  iio01rl  iio01sj
iio01sk  iio01sl  iio01pj  iio01pk  iio01pl  iio01rj  iio01rk
iio01rl  iio01sj  iio01sk  iio01sl  iio01pj  iio01pk  iio01pl
iio01rj  iio01rk  iio01rl  iio01sj  iio01sk  iio01sl  iio01pj
iio01pk  iio01pl  iio01rj  iio01rk  iio01rl  iio01sj  iio01sk
iio01sl  iio01pj  iio01pk  iio01pl  iio01rj  iio01rk  iio01rl
iio01sj  iio01sk  iio01sl  iio01pj  iio01pk  iio01pl  iio01rj
iio01rk  iio01rl  iio01sj  iio01sk  iio01sl  iio01pj  iio01pk
iio01pl  iio01rj  iio01rk  iio01rl  iio01sj  iio01sk  iio01sl
iio01pj  iio01pk  iio01pl  iio01rj  iio01rk  iio01rl  iio01sj
iio01sk  iio01sl  iio01pj  iio01pk  iio01pl  iio01rj  iio01rk
iio01rl  iio01sj  iio01sk  iio01sl
Would you like to try another number? (Y/N)
<span style="font-weight: bold">n</span></code></pre>

      <p>Before continuing on with this assignment, make sure you have done all of the associated reading and understand how the code we've provided should work. The program can be found in the file <code>generate.c</code>.</p>
    </article>

    <article>
      <h2>Assignment</h2>

      <p>The first step in the debugging process is the compilation of the
        program. In the process of compiling code, useful error messages are
        provided that identify problematic parts of the code that are not
        syntactically correct. Very often, these mistakes are typos.</p>

      <p>If you haven't done so already, download <code>generate.c</code> from the starter code. To compile the program type:</p>

      <pre v-highlightjs><code class="bash">$ gcc -g -Wall generate.c</code></pre>

      <blockquote class="note">
        The <code>-g</code> option links the source code to the executable, so
        that you have extra debugging information when using <code>gdb</code>.
        The <code>-Wall</code> option turns on warnings.
      </blockquote>

      <p>You should see something similar to the following (your output may not
        be exactly the same):</p>

      <pre v-highlightjs><code class="plain">generate.c: In function ‘main’:
generate.c:34:5: error: expected ‘;’ before ‘puts’
generate.c:37:5: warning: ‘gets’ is deprecated [-Wdeprecated-declarations]
generate.c:46:5: warning: ‘gets’ is deprecated [-Wdeprecated-declarations]
generate.c:54:5: warning: ‘gets’ is deprecated [-Wdeprecated-declarations]
generate.c: In function ‘print_if_good’:
generate.c:165:5: warning: value computed is not used [-Wunused-value]
generate.c: At top level:
generate.c:194:1: warning: return type defaults to ‘int’ [-Wreturn-type]
generate.c:194:1: warning: conflicting types for ‘convert’ [enabled by default]
generate.c:8:6: note: previous declaration of ‘convert’ was here</code></pre>

      <p>The numbers after the filename refer to the numbers of lines that
        contain errors. "Errors" typically signify something that is syntactically
        incorrect. "Warnings" are issued for code that the C compiler believes to
        be problematic but that is syntactically correct. Warnings will not prevent
        your code from compiling, but they can tip you off to some rather serious
        problems.</p>

      <blockquote>
        <h3 style="color: #004780; margin: 0">Question 1</h3>

        <p>Use the text editor of your choice, correct the errors. What changes
          did you make? You may need to make small changes and recompile to see if
          those errors are fixed.  This may take several iterations. It is okay not
          to fix all the warnings but all of the non-warning problems will need to
          be fixed.</p>

        <p>If you reach this point:</p>

        <pre v-highlightjs><code class="plain">generate.c: In function ‘main’:
generate.c:36:5: warning: ‘gets’ is deprecated [-Wdeprecated-declarations]
generate.c:45:5: warning: ‘gets’ is deprecated [-Wdeprecated-declarations]
generate.c:53:5: warning: ‘gets’ is deprecated [-Wdeprecated-declarations]
generate.c: In function ‘print_if_good’:
generate.c:164:5: warning: value computed is not used [-Wunused-value]
...</code></pre>

        <p>Your program has compiled successfully; don't try to figure out what's
          wrong with this line yet. We'll figure it out eventually.</p>
      </blockquote>

      <p>Let's see if the program works. To run the compiled program type the
        following (words you type are in bold):</p>

      <pre v-highlightjs><code class="plain">$ <span style="font-weight: bold">./a.out</span>
This program finds words associated with phone numbers.
What is the maximum number of consonants you would like
the generated word to possess?
(note: 0's and 1's are considered consonants)
<span style="font-weight: bold">4</span>
Please enter a 7 digit phone number with dashes removed</code></pre>

      <blockquote>
        <h3 style="color: #004780; margin: 0">Question 2</h3>
        <p>The program has one or more bugs. How does the output suggest
          something is wrong?</p>
      </blockquote>

      <p>We will now use <code>gdb</code> to begin debugging. To start
        <code>gdb</code> debugging this program, type:</p>

      <pre v-highlightjs><code class="plain">$ <span style="font-weight: bold">gdb ./a.out</span>
GNU gdb (Ubuntu 7.7.1-0ubuntu5~14.04.3) 7.7.1
Copyright (C) 2014 Free Software Foundation, Inc.
License GPLv3+: GNU GPL version 3 or later &lt;http://gnu.org/licenses/gpl.html&gt;
This is free software: you are free to change and redistribute it.
There is NO WARRANTY, to the extent permitted by law.  Type "show copying"
and "show warranty" for details.
This GDB was configured as "x86_64-linux-gnu".
Type "show configuration" for configuration details.
For bug reporting instructions, please see:
&lt;http://www.gnu.org/software/gdb/bugs/&gt;.
Find the GDB manual and other documentation resources online at:
&lt;http://www.gnu.org/software/gdb/documentation/&gt;.
For help, type "help".
Type "apropos word" to search for commands related to "word"...
Reading symbols from ./a.out...done.
(gdb)</code></pre>

      <p>Before we begin running the program, we will need to set a
        <code>breakpoint</code>. A breakpoint is a programmer-specified point in
        the code where we would like the program to "pause" at while the program is
        running. Why would we want to do this? Using a debugger, we may examine
        variables in this paused state which will give us important information
        about whether or not the program is working correctly.  Type:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">break main</span>
Breakpoint 1 at 0x400685: file generate.c, line 18.</code></pre>

      <p>This will ask the program to stop at the first executed line of code of
        the <code>main()</code> function. To run the program and have it stop at
        this point, type:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">run</span>
Starting program: a.out

Breakpoint 1, main () at generate.c:18
18  {</code></pre>

      <p>Notice that the program has stopped right before it is about to execute
        a line of code. In this case, the line of code in question is line 18 (your
        output may vary), which is just a curly bracket <code>{</code>. Let's go to
        the next line of code to find something more interesting:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">next</span>
24    goahead = 1;</code></pre>

      <p>The <code>next</code> command executes the line of code that was stopped
        at, and then stops the program at the very next line of code.</p>

      <blockquote class="note">
        Notice that the debugger appears to have skipped several lines of code
        from line 18 to line 24 even though we only instructed it to proceed to
        the next line of code. This is because <dfn>variable declarations</dfn>
        are processed immediately.
      </blockquote>

      <p>Let's find out what the value of <code>goahead</code> is. Type the
        following:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">print goahead</span>
$1 = -1073785707</code></pre>

      <p>In the above case, the variable <code>goahead</code> is storing a
        garbage integer (your output will definitely vary here!). Remember, if you
        don't assign a variable an initial value, its contents will be
        unpredictable. Now type:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">next</span>
25    while (goahead)</code></pre>

      <p>To make sure the assignment <code>goahead = 1</code> has actually taken
        place, we can again view the contents of <code>goahead</code>:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">print goahead</span>
$2 = 1</code></pre>

      <p>Sometimes we may want to find out the value of a variable after each
        statement. <code>gdb</code> provides this functionality in its
        <code>display</code> command. Type:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">display goahead</span>
1: goahead = 1
(gdb) <span style="font-weight: bold">next</span>
32      puts("This program finds words that can be associated with phone numbers.");
1: goahead = 1</code></pre>

      <p>Continue executing the program line by line using the <code>next</code>
        command.</p>

      <blockquote class="note">
        In <code>gdb</code>, hitting <kbd>return</kbd> re-executes the most
        recent command; this facility simplifies the process of monitoring a long
        stretch of code.
      </blockquote>

      <p>Stop when you see the following:</p>

      <pre v-highlightjs><code class="plain">(note: 0's and 1's are considered consonants)
36      gets(input);
1: goahead = 1</code></pre>

      <p>Type the following:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">display input</span>
2: input = "38!(@#*(#*!(@#1"
(gdb) <span style="font-weight: bold">next</span>
<span style="font-weight: bold">4</span>
42      maxNumConsonant = input[0] - '0';
2: input = "4\000!(@#*(#*!(@#1"
1: goahead = 1</code></pre>

      <p>Notice that the contents of the string <code>input</code> has changed.
        Since strings are null-terminated, when <code>gets</code> reads a string,
        it copies the user's input into the array passed into the function and
        places a null terminator (denoted by <code>\000</code> in the display
        portion) into the array when it reads a carriage return.</p>

      <blockquote>
        <h3 style="color: #004780; margin: 0">Question 3</h3>
        <p>Using the techniques you have learned so far, explain what
          <code>gets</code> copied into <code>maxNumConsonant</code> after the line
          above is executed.  What <code>gdb</code> commands did you use to find
          out what was copied into <code>maxNumConsonant</code>? Explain what
          <code>input[0] - '0'</code> does.</p>
      </blockquote>

      <p>Continue executing the code line by line. You will be prompted to enter
        a phone number with dashes removed somewhere in the process. After the
        program has read the input phone number, you may want to verify that the
        appropriate information was read. Stop when you reach the following
        line:</p>

      <pre v-highlightjs><code class="plain">48      generateSpellings(phone, maxNumConsonant);</code></pre>

      <p><code>generateSpellings</code> is a defined function within this
        program. We may be interested in watching what happens within the
        <code>generateSpellings</code> function. If we type <code>next</code> at
        this point, we will go to the next line of code instead of going inside the
        <code>generateSpellings</code> function. In order to go inside
        <code>generateSpellings</code>, we can use the <code>step</code>
        command:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">step</span>
generateSpellings (phone=0x7fffffffe060 "4460175", maxNumConsonant=4)
    at generate.c:75
75  {</code></pre>

      <p>Several things happen now. First, the variables that you painstakingly
        "displayed" one by one are no longer refreshed. The reason is because those
        variables are not accessible from within the <code>generateSpellings</code>
        function. The line above shows the contents of the two variables passed
        into the function: <code>phone</code> and <code>maxNumConsonant</code>.
        Your output may be somewhat different.</p>

      <p>For your information, <code>0x7fffffffe060</code> is the hexadecimal
        (base 16) equivalent for 140,737,488,347,232. <code>gdb</code> denotes
        memory addresses by the <code>0x</code> prefix. As you can see, the
        hexadecimal format is a more efficient way of representing large
        numbers.</p>

      <blockquote>
        <h3 style="color: #004780; margin: 0">Question 4</h3>
        <p>What is the value stored in <code>phone</code>? (Hint: Everyone's
          answer will be different.) Why is <code>phone</code> represented by an
          address?</p>
      </blockquote>

      <p>Continue using the <code>next</code> command until you see:</p>

      <pre v-highlightjs><code class="plain">96     convert (phone, phone_int);</code></pre>

      <p>Let's step into this function as well:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">step</span>
convert (phone=0x7fffffffe060 "4460175", phone_int=0x7fffffffdfd0)
    at generate.c:196
196   for (i = 0; i &lt; 7; i++)</code></pre>

      <p>When stepping into a function, <code>gdb</code> does not display the
        contents of an integer array when it is passed in as an argument. This is
        unlike what it does with character arrays. (Note the difference between
        <code>phone</code> and <code>phone_int</code>.) However, regardless of
        variable type, <code>gdb</code> will always show you what the value of that
        variable is when it is passed into the function. If this doesn't make
        sense, then you didn't answer Question 4 correctly.</p>

      <p>In the next five lines is a <code>for</code> loop. It is often helpful
        to monitor a <code>for</code> loop's progress. Type the following:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">display i</span>
3: i = 74128
(gdb) <span style="font-weight: bold">display phone_int[0]</span>
4: phone_int[0] = 268530816
(gdb) <span style="font-weight: bold">display phone_int[1]</span>
5: phone_int[1] = 4199972
(gdb) <span style="font-weight: bold">display phone_int[2]</span>
6: phone_int[2] = 8192
(gdb) <span style="font-weight: bold">display phone_int[3]</span>
7: phone_int[3] = 0
(gdb) <span style="font-weight: bold">display phone_int[4]</span>
8: phone_int[4] = 843604063
(gdb) <span style="font-weight: bold">display phone_int[5]</span>
9: phone_int[5] = 0
(gdb) <span style="font-weight: bold">display phone_int[6]</span>
10: phone_int[6] = 268436928</code></pre>

      <p>Again, because values have not been assigned to the array cells above,
        their contents are in fact completely arbitrary. Your output may differ
        somewhat.</p>

      <p>Monitoring the <code>for</code> loop's progress generally means stepping
        through it via repeated <code>step</code> or <code>next</code>
        commands.</p>

      <blockquote>
        <h3 style="color: #004780; margin: 0">Question 5</h3>
        <p>Use the techniques that you've learned thus far and determine whether
          or not the <code>for</code> loop in this function works. What evidence
          suggests so?</p>
      </blockquote>

      <p>When you exit the function, you should see the following:</p>

      <pre v-highlightjs><code class="plain">generateSpellings (phone=0x7fffffffe060 "4460175", maxNumConsonant=4)
    at generate.c:103
103   for (a = 0; a &lt; 3; a++)</code></pre>

      <p>The statement above lets you know that you've returned to the calling
        function (in this case, <code>generateSpellings</code>).</p>

      <blockquote>
        <h3 style="color: #004780; margin: 0">Question 6</h3>
        <p>Starting on the above line, a series of nested loops is encountered.
          How does each nested loop work? Make sure you step through the loop and
          demonstrate that you've actually examined how the loop works.</p>
      </blockquote>

      <p>Continue executing the code line by line until you reach</p>

      <pre v-highlightjs><code class="plain">131                  print_if_good(maxNumConsonant, word, &amp;level);</code></pre>

      <blockquote>
        <h3 style="color: #004780; margin: 0">Question 7</h3>
        <p>What is the current value of <code>word</code>? Which <code>gdb</code>
          command did you use to find its value? Is its value correct?</p>
      </blockquote>

      <blockquote>
        <h3 style="color: #004780; margin: 0">Question 8</h3>
        <p>In line 131, the address of the variable <code>level</code> is passed
          into <code>print_if_good</code> as opposed to the variable's contents.
          The comments explain that this is a technique that allows the function to
          change the contents of a variable passed into a function. Explain how
          passing a pointer to a variable accomplishes this.</p>
      </blockquote>

      <p>Step into this function using the <code>step</code> command:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">step</span>
print_if_good (maxNumConsonant=4, word=0x7fffffffe020 "ggm01pj",
    level=0x7fffffffdfb0) at generate.c:147
147   int numVowel = 0;</code></pre>

      <p>In the following code, there is a <code>for</code> loop that counts the
        number of vowels and an <code>if</code> statement that determines whether
        or not the generated word is printed. We are interested in testing what
        happens when the <code>if</code> condition is true. Since we may not enter
        the condition every time, we could end up typing <code>next</code> many
        times before we reach the statements within the <code>if</code> condition.
        One way to skip to the "interesting" part of the program is to execute the
        following commands:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">break 161</span>
Breakpoint 2 at 0x400a1e: file generate.c, line 161.
(gdb) <span style="font-weight: bold">continue</span>
Continuing.</code></pre>

      <p>In the above <code>gdb</code> statements, we have asked the program to
        stop at line 161. Line 161 is the call to <code>printf</code> within the
        <code>if</code> statement. The <code>continue</code> statement tells the
        program to run at full speed until the next breakpoint is reached-in this
        case, when we next execute line 161.</p>

      <p>You should eventually see something similar to</p>

      <pre v-highlightjs><code class="plain">Breakpoint 2, print_if_good (maxNumConsonant=4, word=0x7fffffffe020 "iio01pj",
    level=0x7fffffffdfb0) at generate.c:161
161     printf("%s  ",word);</code></pre>

      <p>Print the word and go to the next line:</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">next</span>
164     *level++;</code></pre>

      <p>Now find out what the value of <code>level</code> and
        <code>*level</code> is (your output will vary):</p>

      <pre v-highlightjs><code class="plain">(gdb) <span style="font-weight: bold">display level</span>
11: level = (int *) 0x7fffffffdfb0
(gdb) <span style="font-weight: bold">display *level</span>
12: *level = 0</code></pre>

      <p>The hexadecimal number <code>0x7fffffffdfb0</code> is
        140,737,488,347,056 in decimal. Let's find out what happens when we execute
        this line.</p>

      <pre v-highlightjs><code class="plain">168     if ((*level) % 7 == 0)
12: *level = 0
11: level = (int *) 0x7fffffffdfb4</code></pre>

      <p>The value of <code>level</code> is now <code>0x7fffffffdfb4</code> or in
        decimal, 140,737,488,347,060. <code>*level</code> is still 0. What result
        did we expect? It seems like we might have a problem here.</p>

      <blockquote>
        <h3 style="color: #004780; margin: 0">Question 9</h3>
        <p>Explain what the problem is in this section of code. Fix the problem
          in the code, and recompile the program. What happened to the warning?</p>
      </blockquote>

      <p>The <code>quit</code> command exits <code>gdb</code>.</p>

      <p>When you've recompiled the program, verify the rest of the program works
        as described.</p>

      <p>You will need to turn in your corrected version of
        <code>generate.c</code>, and your answers to the boxed questions in this
        assignment (including any notes you took on your interactions with the
        debugger).</p>
    </article>

    <article>
      <h2>Checklist</h2>
      <p>A satisfactory grade on this assignment requires the following:</p>
      <ul>
        <li>Corrected version of <code>generate.c</code></li>
        <li>Answers to Questions 1 through 9 (including any notes you took on your interactions with the debugger)</li>
      </ul>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/c/Debugging.vue